import { useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { UserPlus, Send, Power, RefreshCcw, Plus, ChevronDown, ChevronRight } from 'lucide-react';
import PageHeader from '../components/PageHeader.jsx';
import ErrorBanner from '../components/ErrorBanner.jsx';
import { useAuth } from '../contexts/AuthContext.jsx';
import { customersAPI, readinessAPI } from '../services/api.js';
import {
  ReadinessBadge,
  ReadinessChecks,
  ReadinessUnavailable,
  ReadinessError
} from '../components/LineReadiness.jsx';
import { formatPhone } from '../lib/format.js';

// Owner-only CUSTOMER MANAGEMENT. Onboard new clients self-serve: "Add
// Customer" creates a tenant + its first admin user and emails a magic-link
// invite. Below, every customer is listed with its users + status; the owner
// can add more users, resend an invite, and deactivate/reactivate.
//
// Gated client-side (redirect non-owners) AND server-side (OWNER_EMAILS → 403).

const PLANS = ['starter', 'core', 'premium', 'enterprise'];
const ROLES = ['admin', 'member', 'viewer'];

export default function Customers() {
  const { isOwner } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  // Add-customer form
  const [form, setForm] = useState({
    companyName: '',
    adminEmail: '',
    plan: 'core',
    industry: '',
    invite: true
  });
  const [creating, setCreating] = useState(false);

  // Per-tenant "add user" inline forms, keyed by tenantId.
  const [userForms, setUserForms] = useState({});
  const [busy, setBusy] = useState({}); // tenantId/action → bool

  // Line readiness, swept for every tenant in ONE call (core does one LiveKit
  // read and one Telnyx read for the whole sweep, not one per number).
  const [readiness, setReadiness] = useState(null);
  const [readinessMissing, setReadinessMissing] = useState(false);
  const [readinessError, setReadinessError] = useState('');
  const [readinessLoading, setReadinessLoading] = useState(false);
  const [openReadiness, setOpenReadiness] = useState({}); // tenantId -> bool

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const r = await customersAPI.list();
      setCustomers(r?.data || []);
    } catch (err) {
      setError(err.message || 'Failed to load customers.');
    } finally {
      setLoading(false);
    }
  };

  // Kept OFF the customer-list path on purpose: readiness calls out to LiveKit
  // and Telnyx, so it is the slow, failure-prone half. If it fails, the customer
  // list still renders — it just carries no verdicts.
  const loadReadiness = async () => {
    setReadinessLoading(true);
    try {
      const r = await readinessAPI.sweep();
      setReadiness(r?.data || r || null);
      setReadinessMissing(false);
      setReadinessError('');
    } catch (err) {
      // 404 = routes not deployed. Anything else is a real failure and has to be
      // said out loud — rendering nothing would look identical to "no feature".
      setReadinessMissing(err.status === 404);
      setReadinessError(err.status === 404 ? '' : err.message || 'check failed');
      setReadiness(null);
    } finally {
      setReadinessLoading(false);
    }
  };

  useEffect(() => {
    if (isOwner) {
      load();
      loadReadiness();
    }
  }, [isOwner]);

  // tenantId -> that tenant's readiness result. No entry means NO VERDICT, which
  // renders as nothing at all: a customer must never look set up because we
  // failed to check.
  const readinessByTenant = useMemo(() => {
    const map = {};
    for (const t of readiness?.tenants || []) map[t.tenantId] = t;
    return map;
  }, [readiness]);

  // Counted from the tenant rows rather than from `liveCount`/`blockedCount`
  // alone, because those two omit UNVERIFIED tenants — "0 live · 0 not live of 6"
  // when LiveKit is unreachable reads as a catastrophe instead of a failed check.
  const readinessSummary = useMemo(() => {
    const rows = readiness?.tenants;
    if (!Array.isArray(rows) || rows.length === 0) return '';
    const live = rows.filter((t) => t.status === 'live').length;
    const blocked = rows.filter((t) => t.status === 'blocked').length;
    const unverified = rows.length - live - blocked;
    return (
      `${live} live · ${blocked} not live` +
      (unverified > 0 ? ` · ${unverified} unverified` : '') +
      ` of ${rows.length}`
    );
  }, [readiness]);


  const canCreate = form.companyName.trim() && /\S+@\S+\.\S+/.test(form.adminEmail);

  const createCustomer = async (e) => {
    e.preventDefault();
    if (!canCreate) return;
    setCreating(true);
    setError('');
    setNotice('');
    try {
      const r = await customersAPI.create({
        companyName: form.companyName.trim(),
        adminEmail: form.adminEmail.trim(),
        plan: form.plan,
        industry: form.industry.trim() || undefined,
        invite: form.invite
      });
      const t = r?.data?.tenant;
      setNotice(
        `Created ${t?.companyName} (${t?.subdomain}).` +
          (r?.data?.invited ? ` Invite sent to ${form.adminEmail}.` : '')
      );
      setForm({ companyName: '', adminEmail: '', plan: 'core', industry: '', invite: true });
      await load();
    } catch (err) {
      setError(err.body?.message || err.message || 'Failed to create customer.');
    } finally {
      setCreating(false);
    }
  };

  const setUserForm = (tid, patch) =>
    setUserForms((f) => ({ ...f, [tid]: { email: '', role: 'member', ...(f[tid] || {}), ...patch } }));

  const addUser = async (tid) => {
    const uf = userForms[tid] || {};
    if (!/\S+@\S+\.\S+/.test(uf.email || '')) return;
    setBusy((b) => ({ ...b, [`add:${tid}`]: true }));
    setError('');
    setNotice('');
    try {
      const r = await customersAPI.addUser(tid, {
        email: uf.email.trim(),
        role: uf.role || 'member',
        invite: true
      });
      setNotice(`Added ${r?.data?.user?.email}.` + (r?.data?.invited ? ' Invite sent.' : ''));
      setUserForms((f) => ({ ...f, [tid]: { email: '', role: 'member' } }));
      await load();
    } catch (err) {
      setError(err.body?.message || err.message || 'Failed to add user.');
    } finally {
      setBusy((b) => ({ ...b, [`add:${tid}`]: false }));
    }
  };

  const resendInvite = async (tid, email) => {
    setBusy((b) => ({ ...b, [`invite:${email}`]: true }));
    setError('');
    setNotice('');
    try {
      await customersAPI.invite(tid, email);
      setNotice(`Invite re-sent to ${email}.`);
    } catch (err) {
      setError(err.body?.message || err.message || 'Failed to send invite.');
    } finally {
      setBusy((b) => ({ ...b, [`invite:${email}`]: false }));
    }
  };

  const toggleStatus = async (tid, current) => {
    const next = current === 'active' ? 'suspended' : 'active';
    setBusy((b) => ({ ...b, [`status:${tid}`]: true }));
    setError('');
    setNotice('');
    try {
      await customersAPI.setStatus(tid, next);
      await load();
    } catch (err) {
      setError(err.body?.message || err.message || 'Failed to change status.');
    } finally {
      setBusy((b) => ({ ...b, [`status:${tid}`]: false }));
    }
  };

  const sorted = useMemo(
    () => [...customers].sort((a, b) => (a.companyName || '').localeCompare(b.companyName || '')),
    [customers]
  );

  // ⛔ THE OWNER GUARD MUST SIT BELOW EVERY HOOK, and it used to sit above this useMemo.
  //
  // React identifies hooks by CALL ORDER. Returning early for a non-owner meant this
  // component ran one fewer hook on those renders — fine while `isOwner` never changes,
  // and a crash the moment it does. It does: AuthContext bootstraps asynchronously, so a
  // cold load can render once with isOwner false and again with it true. React then
  // throws "rendered more hooks than during the previous render" and the page dies —
  // for the OWNER, which is the only person this page is for.
  //
  // Found by eslint react-hooks/rules-of-hooks on its first run, 2026-08-16.
  if (!isOwner) return <Navigate to="/" replace />;

  return (
    <div className="p-6 md:p-10 max-w-5xl">
      <PageHeader
        title="Customers"
        subtitle="Onboard and manage client accounts."
        actions={
          <button
            type="button"
            className="btn-ghost"
            onClick={() => {
              load();
              loadReadiness();
            }}
            disabled={loading}
          >
            <RefreshCcw size={15} /> Refresh
          </button>
        }
      />

      {error && <ErrorBanner message={error} onDismiss={() => setError('')} />}
      {notice && (
        <div className="mb-6 rounded-md border border-emerald-800 bg-emerald-950/40 px-4 py-3 text-sm text-emerald-200">
          {notice}
        </div>
      )}

      {/* Add customer */}
      <section className="mb-10 rounded-lg border border-stone-800 bg-stone-950/40 p-5">
        <h2 className="mb-4 flex items-center gap-2 text-lg text-stone-100">
          <Plus size={18} /> Add Customer
        </h2>
        <form onSubmit={createCustomer} className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="mb-1 block text-sm text-stone-300">Company name</label>
            <input
              className="field-input"
              value={form.companyName}
              onChange={(e) => setForm((f) => ({ ...f, companyName: e.target.value }))}
              placeholder="Acme Health"
              disabled={creating}
            />
          </div>
          <div>
            <label className="mb-1 block text-sm text-stone-300">Admin email</label>
            <input
              type="email"
              className="field-input"
              value={form.adminEmail}
              onChange={(e) => setForm((f) => ({ ...f, adminEmail: e.target.value }))}
              placeholder="owner@acme.com"
              disabled={creating}
            />
          </div>
          <div>
            <label className="mb-1 block text-sm text-stone-300">Plan</label>
            <select
              className="field-input"
              value={form.plan}
              onChange={(e) => setForm((f) => ({ ...f, plan: e.target.value }))}
              disabled={creating}
            >
              {PLANS.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-sm text-stone-300">Industry (optional)</label>
            <input
              className="field-input"
              value={form.industry}
              onChange={(e) => setForm((f) => ({ ...f, industry: e.target.value }))}
              placeholder="Healthcare"
              disabled={creating}
            />
          </div>
          <label className="flex items-center gap-2 text-sm text-stone-300 sm:col-span-2">
            <input
              type="checkbox"
              checked={form.invite}
              onChange={(e) => setForm((f) => ({ ...f, invite: e.target.checked }))}
              disabled={creating}
            />
            Email a magic-link invite to the admin now
          </label>
          <div className="sm:col-span-2">
            <button type="submit" className="btn-primary" disabled={!canCreate || creating}>
              <UserPlus size={16} /> {creating ? 'Creating…' : 'Create customer'}
            </button>
          </div>
        </form>
      </section>

      {/* Customer list */}
      <section>
        <div className="mb-4 flex flex-wrap items-baseline justify-between gap-2">
          <h2 className="text-lg text-stone-100">All customers</h2>
          {readinessLoading ? (
            <span className="text-xs text-stone-500">Checking lines…</span>
          ) : readinessMissing ? (
            <ReadinessUnavailable />
          ) : readinessError ? (
            <ReadinessError message={readinessError} />
          ) : readinessSummary ? (
            <span className="text-xs text-stone-500">{readinessSummary}</span>
          ) : null}
        </div>
        {loading ? (
          <p className="text-sm text-stone-500">Loading…</p>
        ) : sorted.length === 0 ? (
          <p className="text-sm text-stone-500">No customers yet.</p>
        ) : (
          <div className="space-y-4">
            {sorted.map((c) => {
              const uf = userForms[c.id] || { email: '', role: 'member' };
              const suspended = c.status !== 'active';
              const ready = readinessByTenant[c.id] || null;
              const readyOpen = !!openReadiness[c.id];
              return (
                <div
                  key={c.id}
                  className="rounded-lg border border-stone-800 bg-stone-950/40 p-5"
                >
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-stone-100">{c.companyName}</span>
                        <span
                          className={`rounded px-2 py-0.5 text-xs ${
                            suspended
                              ? 'bg-rose-950/60 text-rose-300'
                              : 'bg-emerald-950/60 text-emerald-300'
                          }`}
                        >
                          {c.status}
                        </span>
                        <span className="text-xs uppercase tracking-wide text-stone-500">
                          {c.plan}
                        </span>
                        {/* Line readiness. Green ONLY when every blocker check passed;
                            no badge at all when we have no verdict. */}
                        {ready && <ReadinessBadge status={ready.status} />}
                      </div>
                      <div className="mt-1 text-xs text-stone-500">{c.subdomain}</div>
                    </div>
                    <button
                      type="button"
                      className="btn-ghost"
                      onClick={() => toggleStatus(c.id, c.status)}
                      disabled={busy[`status:${c.id}`]}
                    >
                      <Power size={15} /> {suspended ? 'Reactivate' : 'Deactivate'}
                    </button>
                  </div>

                  {/* Lines — is this customer actually reachable? */}
                  {ready && (
                    <div className="mt-3 border-t border-stone-900 pt-3">
                      {(ready.numbers || []).length === 0 ? (
                        <p className="text-xs text-stone-500">
                          No phone number on this account — nothing rings yet.
                        </p>
                      ) : (
                        <ul className="space-y-1">
                          {ready.numbers.map((n) => (
                            <li
                              key={n.did}
                              className="flex flex-wrap items-center gap-2 text-sm"
                            >
                              <span className="text-stone-200">{formatPhone(n.did)}</span>
                              <ReadinessBadge status={n.status} />
                              {n.status === 'blocked' && (
                                <span className="text-xs text-rose-300">
                                  {n.blockers?.length || 0} blocker
                                  {n.blockers?.length === 1 ? '' : 's'}
                                </span>
                              )}
                            </li>
                          ))}
                        </ul>
                      )}

                      {ready.scheduleReady === false && (
                        <p className="mt-2 text-xs text-amber-300">
                          Campaign scheduling is blocked until a timezone is chosen.
                        </p>
                      )}

                      <button
                        type="button"
                        className="mt-2 flex items-center gap-1 text-xs text-stone-400 hover:text-alfred-gold"
                        onClick={() =>
                          setOpenReadiness((s) => ({ ...s, [c.id]: !s[c.id] }))
                        }
                      >
                        {readyOpen ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
                        {readyOpen ? 'Hide what was checked' : 'What was checked'}
                      </button>
                      {readyOpen && (
                        <div className="mt-2 rounded-lg border border-stone-800 bg-stone-950/40 p-3">
                          {/* A failed tenant-wide check next to a green number is not a
                              contradiction: a DID served by a demonstration or by a
                              separate deployment is held to THAT, not to the tenant's
                              own config. Said plainly so it doesn't read as a bug. */}
                          <p className="mb-2 text-xs text-stone-500">
                            Account-wide checks apply to any line that answers from this
                            account’s own configuration. A number served by a demonstration
                            or a separate deployment is judged on that instead — so a
                            failure here can sit next to a line that rings fine.
                          </p>
                          <ReadinessChecks checks={ready.checks} showPassing />
                        </div>
                      )}
                    </div>
                  )}

                  {/* Users */}
                  <div className="mt-4 border-t border-stone-800 pt-4">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-xs uppercase tracking-wide text-stone-500">
                          <th className="pb-2 font-medium">User</th>
                          <th className="pb-2 font-medium">Role</th>
                          <th className="pb-2 font-medium">Status</th>
                          <th className="pb-2" />
                        </tr>
                      </thead>
                      <tbody>
                        {(c.users || []).map((u) => (
                          <tr key={u.id} className="border-t border-stone-900">
                            <td className="py-2 text-stone-200">
                              {u.email}
                              {u.name && (
                                <span className="ml-2 text-stone-500">{u.name}</span>
                              )}
                            </td>
                            <td className="py-2 capitalize text-stone-400">{u.role}</td>
                            <td className="py-2 text-stone-400">
                              {u.lastLoginAt
                                ? 'Active'
                                : u.emailVerified
                                  ? 'Verified'
                                  : 'Invited'}
                            </td>
                            <td className="py-2 text-right">
                              <button
                                type="button"
                                className="btn-ghost"
                                onClick={() => resendInvite(c.id, u.email)}
                                disabled={busy[`invite:${u.email}`]}
                              >
                                <Send size={14} /> Resend invite
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>

                    {/* Add user */}
                    <div className="mt-3 flex flex-wrap items-end gap-2">
                      <input
                        type="email"
                        className="field-input flex-1 min-w-[200px]"
                        placeholder="new.user@company.com"
                        value={uf.email}
                        onChange={(e) => setUserForm(c.id, { email: e.target.value })}
                      />
                      <select
                        className="field-input w-32"
                        value={uf.role}
                        onChange={(e) => setUserForm(c.id, { role: e.target.value })}
                      >
                        {ROLES.map((r) => (
                          <option key={r} value={r}>
                            {r}
                          </option>
                        ))}
                      </select>
                      <button
                        type="button"
                        className="btn-ghost"
                        onClick={() => addUser(c.id)}
                        disabled={busy[`add:${c.id}`] || !/\S+@\S+\.\S+/.test(uf.email || '')}
                      >
                        <UserPlus size={14} /> Add user
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}
